import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { BookOpen, ArrowLeft, Clock, Tag, ChevronRight, Search, Home, PenLine } from 'lucide-react';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { Seo } from '../components/seo/Seo';
import { FaqSection } from '../components/seo/FaqSection';
import { FAQS } from '../components/seo/faqData';

const API = process.env.REACT_APP_BACKEND_URL;

const categoryColors = {
    schlaf: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
    ernaehrung: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    fitness: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
    mental: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
    gesundheit: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    nanouk: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',
    tutorial: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    app: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
};

export default function Blog() {
    const { slug } = useParams();
    return slug ? <BlogPost slug={slug} /> : <BlogList />;
}

function BlogList() {
    const { language } = useLanguage();
    const { user } = useAuth();
    const [posts, setPosts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCat, setSelectedCat] = useState(null);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const t = language === 'de';

    useEffect(() => { loadPosts(); loadCategories(); }, [language, selectedCat]);

    const loadPosts = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ language, limit: '50' });
            if (selectedCat) params.set('category', selectedCat);
            const res = await axios.get(`${API}/api/blog/posts?${params}`);
            setPosts(res.data.posts || []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const loadCategories = async () => {
        try {
            const res = await axios.get(`${API}/api/blog/categories?language=${language}`);
            setCategories(res.data.categories || []);
        } catch (e) {}
    };

    const filtered = search ? posts.filter(p => 
        p.title.toLowerCase().includes(search.toLowerCase()) ||
        p.excerpt?.toLowerCase().includes(search.toLowerCase())
    ) : posts;

    return (
        <div className="p-4 lg:p-8 pb-24 lg:pb-8" data-testid="blog-page" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)' }}>
            <Seo
                title={t ? 'Wellness-Blog – Schlaf, Ernährung, Fitness & Gesundheit' : 'Wellness Blog – Sleep, Nutrition, Fitness & Health'}
                description={t
                    ? 'Evidenzbasierte Wellness-Artikel von Nanouk: Schlaf, Ernährung, Fitness, Mental & Gesundheit – kostenlos und ohne Anmeldung lesbar.'
                    : 'Evidence-based wellness articles by Nanouk: sleep, nutrition, fitness, mental & health – free to read, no account needed.'}
                path={language === 'en' ? '/en/blog' : '/blog'}
                language={language}
                withHreflang
            />
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto space-y-4">
                <div className="flex items-center justify-between gap-2">
                    <h1 className="font-display text-xl font-bold flex items-center gap-2 truncate">
                        <BookOpen className="w-5 h-5 text-primary shrink-0" />
                        Blog
                    </h1>
                    <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-muted-foreground">{filtered.length}</span>
                        {user && (
                            <Link to="/blog-studio">
                                <Button variant="outline" size="sm" className="rounded-full h-9 px-3 gap-1 border-violet-300 text-violet-700 hover:bg-violet-50" data-testid="blog-studio-link">
                                    <PenLine className="w-4 h-4" />
                                    Studio
                                </Button>
                            </Link>
                        )}
                        <Link to="/">
                            <Button variant="default" size="sm" className="rounded-full h-9 px-4 gap-1" data-testid="blog-home-btn">
                                <Home className="w-4 h-4" />
                                App
                            </Button>
                        </Link>
                    </div>
                </div>

                {/* Search */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t ? 'Artikel suchen...' : 'Search articles...'}
                        className="pl-10 bg-muted border-2 border-border"
                    />
                </div>

                {/* Categories */}
                <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
                    <Button variant={!selectedCat ? 'default' : 'outline'} size="sm" className="rounded-full shrink-0" onClick={() => setSelectedCat(null)}>
                        {t ? 'Alle' : 'All'}
                    </Button>
                    {categories.map(c => (
                        <Button key={c.name} variant={selectedCat === c.name ? 'default' : 'outline'} size="sm" className="rounded-full shrink-0" onClick={() => setSelectedCat(c.name)}>
                            {c.name} ({c.count})
                        </Button>
                    ))}
                </div>

                {/* Posts */}
                {loading ? (
                    <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-24 bg-muted animate-pulse rounded-xl" />)}</div>
                ) : filtered.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">{t ? 'Keine Artikel gefunden' : 'No articles found'}</p>
                ) : (
                    <div className="space-y-3">
                        {filtered.map((post) => (
                            <Link key={post.slug} to={`/blog/${post.slug}`}>
                                <Card className="border hover:border-primary/30 transition-colors cursor-pointer mb-3 overflow-hidden">
                                    <CardContent className="p-0">
                                        <div className="flex">
                                            {getPostImage(post) && (
                                                <div className="w-20 h-20 shrink-0">
                                                    <img src={getPostImage(post)} alt={post.title} className="w-full h-full object-cover" />
                                                </div>
                                            )}
                                            <div className="p-3 flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-0.5">
                                                    <Badge className={`text-[10px] ${categoryColors[post.category] || 'bg-muted'}`}>{post.category}</Badge>
                                                    <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />{post.read_time || '5 min'}
                                                    </span>
                                                </div>
                                                <h3 className="font-semibold text-sm line-clamp-2">{post.title}</h3>
                                                <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{post.excerpt}</p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </Link>
                        ))}
                    </div>
                )}

                <FaqSection faqs={FAQS.blog[language]} language={language} className="pt-10" />
            </motion.div>
        </div>
    );
}

// Individual article images - unique per slug, fallback to category
const articleImages = {
    // Schlaf
    'besserer-schlaf-10-tipps': 'https://images.unsplash.com/photo-1531353826977-0941b4779a1c?w=400&h=200&fit=crop',
    'schlafphasen-verstehen': 'https://images.unsplash.com/photo-1705077917740-dacd6ed4e0d7?w=400&h=200&fit=crop',
    'schlaf-und-gewicht': 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=200&fit=crop',
    'perfekte-schlafroutine': 'https://images.unsplash.com/photo-1520206183501-b80df61043c2?w=400&h=200&fit=crop',
    'schlaf-tracking-guide': 'https://images.unsplash.com/photo-1434494878577-86c23bcb06b9?w=400&h=200&fit=crop',
    'koffein-und-schlaf': 'https://images.unsplash.com/photo-1509042239860-f550ce710b93?w=400&h=200&fit=crop',
    'nickerchen-power-nap': 'https://images.unsplash.com/photo-1520206183501-b80df61043c2?w=400&h=200&fit=crop',
    'schlaf-und-immunsystem': 'https://images.unsplash.com/photo-1759025324989-244c6e9ba201?w=400&h=200&fit=crop',
    // Ernährung
    'ernaehrung-basics': 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=400&h=200&fit=crop',
    'wasser-trinken-wichtigkeit': 'https://images.unsplash.com/photo-1548839140-29a749e1cf4d?w=400&h=200&fit=crop',
    'proteinreiche-snacks': 'https://images.unsplash.com/photo-1532550907401-a500c9a57435?w=400&h=200&fit=crop',
    'meal-prep-anfaenger': 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=400&h=200&fit=crop',
    'zucker-reduzieren': 'https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?w=400&h=200&fit=crop',
    'ernaehrung-und-stimmung': 'https://images.unsplash.com/photo-1498837167922-ddd27525d352?w=400&h=200&fit=crop',
    'intermittierendes-fasten': 'https://images.unsplash.com/photo-1504754524776-8f4f37790ca0?w=400&h=200&fit=crop',
    'alkohol-und-gesundheit': 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=400&h=200&fit=crop',
    'superfoods-mythos': 'https://images.unsplash.com/photo-1457530378978-8bac673b8062?w=400&h=200&fit=crop',
    'ernaehrung-im-homeoffice': 'https://images.unsplash.com/photo-1593062096033-9a26b09da705?w=400&h=200&fit=crop',
    // Fitness
    'einsteiger-workout-plan': 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=400&h=200&fit=crop',
    '10000-schritte-mythos': 'https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?w=400&h=200&fit=crop',
    'krafttraining-vorteile': 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=400&h=200&fit=crop',
    'golf-fitness-tipps': 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=400&h=200&fit=crop',
    'wandern-gesundheit': 'https://images.unsplash.com/photo-1696219364361-ba4eac9f2361?w=400&h=200&fit=crop',
    'hiit-training-guide': 'https://images.unsplash.com/photo-1599552683573-9dc48255fe85?w=400&h=200&fit=crop',
    'stretching-mobilitaet': 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=200&fit=crop',
    'sport-und-schlaf': 'https://images.unsplash.com/photo-1511389571644-a2550b195566?w=400&h=200&fit=crop',
    'signaltransduktion-training': 'https://images.unsplash.com/photo-1574680096145-d05b474e2155?w=400&h=200&fit=crop',
    'pilates-chair-training': 'https://images.unsplash.com/photo-1518611012118-696072aa579a?w=400&h=200&fit=crop',
    'altern-krafttraining-lebensqualitaet': 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=200&fit=crop',
    'muskellängentraining-faszien': 'https://images.unsplash.com/photo-1552196563-55cd4e45efb3?w=400&h=200&fit=crop',
    'ausdauertraining-richtig-steuern': 'https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?w=400&h=200&fit=crop',
    'atemtraining-leistung': 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=400&h=200&fit=crop',
    // Mental
    'stress-management-basics': 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=400&h=200&fit=crop',
    'achtsamkeit-anfaenger': 'https://images.unsplash.com/photo-1528715471579-d1bcf0ba5e83?w=400&h=200&fit=crop',
    'digital-detox': 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=200&fit=crop',
    'burnout-erkennen': 'https://images.unsplash.com/photo-1493836512294-502baa1986e2?w=400&h=200&fit=crop',
    'natur-und-gesundheit': 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&h=200&fit=crop',
    'dankbarkeit-praxis': 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=400&h=200&fit=crop',
    'motivation-im-winter': 'https://images.unsplash.com/photo-1491002052546-bf38f186af56?w=400&h=200&fit=crop',
    'mentale-resilienz-aufbauen': 'https://images.unsplash.com/photo-1573836468077-f223c15c5698?w=400&h=200&fit=crop',
    // Gesundheit
    'tcm-fuer-anfaenger': 'https://images.unsplash.com/photo-1512290923902-8a9f81dc236c?w=400&h=200&fit=crop',
    'check-up-ab-40': 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&h=200&fit=crop',
    'kopfschmerzen-natuerlich': 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=400&h=200&fit=crop',
    'immunsystem-staerken': 'https://images.unsplash.com/photo-1498837167922-ddd27525d352?w=400&h=200&fit=crop',
    'vitamin-d-mangel': 'https://images.unsplash.com/photo-1506784365847-bbad939e9335?w=400&h=200&fit=crop',
    'rueckenschmerzen-vorbeugen': 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=200&fit=crop',
    'blutdruck-senken': 'https://images.unsplash.com/photo-1559757175-5700dde675bc?w=400&h=200&fit=crop',
    'erkaeltung-vs-grippe': 'https://images.unsplash.com/photo-1584362917165-526a968579e8?w=400&h=200&fit=crop',
    'knochengesundheit-krafttraining': 'https://images.unsplash.com/photo-1530026405186-ed1f139313f8?w=400&h=200&fit=crop',
    'sauna-immunsystem-training': 'https://images.unsplash.com/photo-1554244933-d876deb6b2ff?w=400&h=200&fit=crop',
    'sturzpraevention-gleichgewicht': 'https://images.unsplash.com/photo-1518611012118-696072aa579a?w=400&h=200&fit=crop',
    'diabetes-fitnesstraining': 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=200&fit=crop',
    // App/Tutorial/Nanouk
    'bestbuddy-einstieg': 'https://images.unsplash.com/photo-1434494878577-86c23bcb06b9?w=400&h=200&fit=crop',
    'nanouk-ki-coach': 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=400&h=200&fit=crop',
    'gamification-bestbuddy': 'https://images.unsplash.com/photo-1511512578047-dfb367046420?w=400&h=200&fit=crop',
    'pdf-arztbericht': 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&h=200&fit=crop',
    'readiness-score-verstehen': 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=200&fit=crop',
    'bestbuddy-update-v35': 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=400&h=200&fit=crop',
    'flow-finden-atemtechnik-sport': 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=400&h=200&fit=crop',
};

const categoryImages = {
    schlaf: 'https://images.unsplash.com/photo-1531353826977-0941b4779a1c?w=400&h=200&fit=crop',
    tutorial: 'https://images.unsplash.com/photo-1434494878577-86c23bcb06b9?w=400&h=200&fit=crop',
    fitness: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=200&fit=crop',
    ernaehrung: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=400&h=200&fit=crop',
    mental: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=400&h=200&fit=crop',
    gesundheit: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&h=200&fit=crop',
    app: 'https://images.unsplash.com/photo-1434494878577-86c23bcb06b9?w=400&h=200&fit=crop',
    nanouk: 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=400&h=200&fit=crop',
};

function getArticleImage(slug, category) {
    return articleImages[slug] || categoryImages[category] || categoryImages['fitness'];
}

const BACKEND = process.env.REACT_APP_BACKEND_URL;
function getPostImage(post) {
    if (post.image_url) return `${BACKEND}${post.image_url}`;
    return getArticleImage(post.slug, post.category);
}

function BlogPost({ slug }) {
    const { language } = useLanguage();
    const navigate = useNavigate();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`${API}/api/blog/posts/${slug}?language=${language}`);
                setPost(res.data);
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        })();
    }, [slug, language]);

    if (loading) return <div className="p-8"><div className="h-64 bg-muted animate-pulse rounded-xl" /></div>;
    if (!post) return <div className="p-8 text-center"><p>Artikel nicht gefunden</p><Button onClick={() => navigate('/blog')} className="mt-4">Zurueck</Button></div>;

    return (
        <div className="p-4 lg:p-8 pb-24 lg:pb-8" data-testid="blog-post-page" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)' }}>
            <Seo
                title={post.title}
                description={post.excerpt}
                path={`/blog/${post.slug}`}
                type="article"
                image={post.image_url || getArticleImage(post.slug, post.category)}
                language={language}
                jsonLd={[
                    {
                        '@context': 'https://schema.org',
                        '@type': 'BlogPosting',
                        headline: post.title,
                        description: post.excerpt,
                        image: post.image_url ? `https://bestbuddy.fun${post.image_url}` : getArticleImage(post.slug, post.category),
                        datePublished: post.published_at,
                        inLanguage: language,
                        author: { '@type': 'Organization', name: 'BESTBUDDY (MYSYMP AG)', url: 'https://bestbuddy.fun' },
                        publisher: { '@type': 'Organization', name: 'MYSYMP AG', logo: { '@type': 'ImageObject', url: 'https://bestbuddy.fun/icons/icon-192x192.png' } },
                        mainEntityOfPage: `https://bestbuddy.fun/blog/${post.slug}`,
                    },
                    {
                        '@context': 'https://schema.org',
                        '@type': 'BreadcrumbList',
                        itemListElement: [
                            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://bestbuddy.fun/' },
                            { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://bestbuddy.fun/blog' },
                            { '@type': 'ListItem', position: 3, name: post.title, item: `https://bestbuddy.fun/blog/${post.slug}` },
                        ],
                    },
                    post.faq && post.faq.length > 0 ? {
                        '@context': 'https://schema.org',
                        '@type': 'FAQPage',
                        inLanguage: language,
                        mainEntity: post.faq.map((f) => ({
                            '@type': 'Question',
                            name: f.q,
                            acceptedAnswer: { '@type': 'Answer', text: f.a },
                        })),
                    } : null,
                ]}
            />
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl mx-auto">
                <div className="flex items-center justify-between mb-4">
                    <Button variant="ghost" onClick={() => navigate('/blog')} className="gap-2" data-testid="blog-back-btn">
                        <ArrowLeft className="w-4 h-4" /> {language === 'de' ? 'Alle Artikel' : 'All Articles'}
                    </Button>
                    <Link to="/">
                        <Button variant="default" size="sm" className="rounded-full h-8 px-3 gap-1">
                            <Home className="w-3.5 h-3.5" /> App
                        </Button>
                    </Link>
                </div>

                <article>
                    {/* Hero Image */}
                    <div className="rounded-2xl overflow-hidden mb-4 h-40 lg:h-56">
                        <img 
                            src={getPostImage(post)}
                            alt={post.title}
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="mb-4">
                        <Badge className={`${categoryColors[post.category] || 'bg-muted'} mb-2`}>{post.category}</Badge>
                        <h1 className="font-display text-2xl lg:text-3xl font-bold mb-2">{post.title}</h1>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{post.read_time || '5 min'}</span>
                            {post.published_at && <span>{new Date(post.published_at).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })}</span>}
                        </div>
                    </div>

                    {/* Content */}
                    <div className="prose prose-sm dark:prose-invert max-w-none space-y-4">
                        {post.content?.split('\n\n').map((paragraph, i) => {
                            if (paragraph.startsWith('## ')) return <h2 key={i} className="text-xl font-bold mt-6 mb-3">{paragraph.replace('## ', '')}</h2>;
                            if (paragraph.startsWith('### ')) return <h3 key={i} className="text-lg font-semibold mt-4 mb-2">{paragraph.replace('### ', '')}</h3>;
                            if (paragraph.startsWith('- ')) return <ul key={i} className="space-y-1 ml-4">{paragraph.split('\n').map((li, j) => <li key={j} className="text-sm text-muted-foreground list-disc">{li.replace('- ', '')}</li>)}</ul>;
                            if (paragraph.startsWith('**FAQ')) return <h2 key={i} className="text-xl font-bold mt-8 mb-4 flex items-center gap-2">FAQ</h2>;
                            if (paragraph.startsWith('**F:')) {
                                const parts = paragraph.split('\n');
                                const q = parts[0]?.replace('**F: ', '').replace('**', '');
                                const a = parts[1]?.replace('A: ', '');
                                return (
                                    <div key={i} className="bg-muted/50 rounded-xl p-4 border border-border">
                                        <p className="font-semibold text-sm mb-1">{q}</p>
                                        <p className="text-sm text-muted-foreground">{a}</p>
                                    </div>
                                );
                            }
                            return <p key={i} className="text-sm text-muted-foreground leading-relaxed">{paragraph}</p>;
                        })}
                    </div>

                    {/* FAQ Section */}
                    {post.faq && post.faq.length > 0 && (
                        <div className="mt-8 space-y-3">
                            <h2 className="text-xl font-bold">FAQ</h2>
                            {post.faq.map((item, i) => (
                                <div key={i} className="bg-muted/50 rounded-xl p-4 border border-border">
                                    <p className="font-semibold text-sm mb-1">{item.q}</p>
                                    <p className="text-sm text-muted-foreground">{item.a}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </article>

                <div className="mt-8 pt-4 border-t border-border text-center">
                    <Button onClick={() => navigate('/blog')} variant="outline" className="rounded-full">
                        {language === 'de' ? 'Weitere Artikel lesen' : 'Read more articles'}
                    </Button>
                </div>
            </motion.div>
        </div>
    );
}
